import { Injectable } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

export interface SelectionOptions<T> {
  trackBy?: (T) => any
  objects?: T[]
}

export interface CheckboxMouseEvent {
  shiftKey: boolean
  preventDefault: () => void
  stopPropagation: () => void
}

export class Selection<T> {
  selectedObjects = new Set<T>()
  selectedKeys = new Set()
  lastSelected: T|null = null
  trackBy: (T) => any
  objects: T[]

  get change$ (): Observable<void> {
    return this.change.pipe(debounceTime(0))
  }
  private change = new Subject<void>()

  constructor (options: SelectionOptions<T>) {
    this.trackBy = options.trackBy ?? (x => x)
    this.objects = options.objects ?? []
  }

  clear (): void {
    this.selectedObjects.clear()
    this.selectedKeys.clear()
    this.change.next()
  }

  setObjects (objects: T[]): void {
    this.objects = objects
    const selection = new Set(this.selectedObjects)
    this.selectedObjects.clear()
    this.selectedKeys.clear()
    for (const object of this.objects) {
      if (selection.has(this.trackBy(object))) {
        this.select(object)
      }
    }
  }

  isSelected (object: T): boolean {
    return this.selectedKeys.has(this.trackBy(object))
  }

  select (object: T): void {
    this.selectedKeys.add(this.trackBy(object))
    this.selectedObjects.add(object)
    this.lastSelected = object
    this.change.next()
  }

  deselect (object: T): void {
    this.selectedKeys.delete(this.trackBy(object))
    this.selectedObjects.delete(object)
    this.lastSelected = object
    this.change.next()
  }

  selectAll (): void {
    this.clear()
    this.invert()
  }

  toggle (object: T): void {
    if (this.isSelected(object)) {
      this.deselect(object)
    } else {
      this.select(object)
    }
  }

  isEmpty (): boolean {
    return this.selectedObjects.size === 0
  }

  get (): T[] {
    return [...this.selectedObjects]
  }

  invert (): void {
    for (const object of this.objects) {
      this.toggle(object)
    }
  }

  toggleAllUntil (object: T): void {
    const fromIndex = this.objects.findIndex(x => x === this.lastSelected)
    const toIndex = this.objects.findIndex(x => x === object)

    // eslint-disable-next-line @typescript-eslint/init-declarations
    let slicedItems: T[]
    if (fromIndex <= toIndex) {
      slicedItems = this.objects.slice(fromIndex, toIndex + 1)
    } else {
      slicedItems = this.objects.slice(toIndex, fromIndex + 1)
    }

    if (this.isSelected(object)) {
      for (const obj of slicedItems) {
        this.deselect(obj)
      }
    } else {
      for (const obj of slicedItems) {
        this.select(obj)
      }
    }
  }

  makeCheckboxHandler (object: T): (CheckboxMouseEvent) => void {
    return (event: MouseEvent) => {
      if (event.shiftKey) {
        this.toggleAllUntil(object)
      } else {
        this.toggle(object)
      }
      event.preventDefault()
      event.stopPropagation()
    }
  }
}

@Injectable({ providedIn: 'root' })
export class SelectionService {
  make <T> (options: SelectionOptions<T> = {}): Selection<T> {
    return new Selection(options)
  }
}
